import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import DatePicker from 'components/DatePicker'
import Modal from 'components/Modal'
import Textarea from 'components/Textarea'

import Api from 'services/api'

import GA4 from 'helpers/ga4'
import { scrubText } from 'helpers/inputCleaners'

import { ButtonContainer } from 'styles/shared'
import { color } from 'styles/colors'

import { DeviceBase } from 'types/Device'

type Props = {
  customerName?:string
  device: DeviceBase
  closeCallback: (device: DeviceBase) => void
}

const ServiceContractModal = ({ customerName, device, closeCallback }: Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState({ apiError: '', serviceContractStartAt: '', serviceContractEndAt: '' })
  const [serviceContractAttributes, setServiceContractAttributes] = useState({
    serviceContractNotes: device.serviceContractNotes,
    serviceContractStatus: device.serviceContractStatus,
    serviceContractStartAt: device.serviceContractStartAt,
    serviceContractEndAt: device.serviceContractEndAt
  })

  const onChangeDate = (attribute, value) => {
    setErrorMessage({ apiError: '', serviceContractStartAt: '', serviceContractEndAt: '' })

    if (attribute === 'startDate') {
      setServiceContractAttributes({
        ...serviceContractAttributes,
        serviceContractStartAt: value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : value
      })
    } else {
      let endDate = value
      let status = ''

      if (!endDate) {
        status = 'None'
      } else if (value && moment(value).isValid() && moment(endDate).isAfter(moment(new Date()))) {
        endDate = moment(value).format('YYYY-MM-DD')
        status = 'Active'
      } else if (value && moment(value).isValid() && !moment(endDate).isAfter(moment(new Date()))) {
        endDate = moment(value).format('YYYY-MM-DD')
        status = 'Expired'
      }

      setServiceContractAttributes({
        ...serviceContractAttributes,
        serviceContractStatus: status,
        serviceContractEndAt: endDate
      })
    }
  }

  const validate = useCallback((serviceContractAttributes) => {
    let error = false
    setErrorMessage({ ...errorMessage, serviceContractStartAt: '', serviceContractEndAt: '' })

    if (serviceContractAttributes.serviceContractStartAt !== null && !moment(serviceContractAttributes.serviceContractStartAt).isValid()) {
      setErrorMessage({ ...errorMessage, serviceContractStartAt: 'Invalid start date', serviceContractEndAt: '' })
      error = true
    } else if (serviceContractAttributes.serviceContractEndAt !== null && !moment(serviceContractAttributes.serviceContractEndAt).isValid()) {
      setErrorMessage({ ...errorMessage, serviceContractStartAt: '', serviceContractEndAt: 'Invalid end date' })
      error = true
    } else if (moment(serviceContractAttributes.serviceContractStartAt).isValid() && !serviceContractAttributes.serviceContractEndAt) {
      setErrorMessage({ ...errorMessage, serviceContractStartAt: '', serviceContractEndAt: 'End date is required' })
      error = true
    } else if ((moment(serviceContractAttributes.serviceContractStartAt).isValid() && moment(serviceContractAttributes.serviceContractStartAt).isValid()) &&
      !moment(serviceContractAttributes.serviceContractEndAt).isAfter(moment(serviceContractAttributes.serviceContractStartAt))) {
      setErrorMessage({ ...errorMessage, serviceContractStartAt: '', serviceContractEndAt: 'End date must be after the start date' })
      error = true
    }

    return error
  }, [errorMessage])

  const onSaveServiceContract = useCallback(async () => {
    setErrorMessage({ apiError: '', serviceContractStartAt: '', serviceContractEndAt: '' })

    const isStatusUpdated = device.serviceContractStatus !== serviceContractAttributes.serviceContractStatus
    const isStartDateUpdated = device.serviceContractStartAt !== serviceContractAttributes.serviceContractStartAt
    const isEndDateUpdated = device.serviceContractEndAt !== serviceContractAttributes.serviceContractEndAt
    const isNotesUpdated = device.serviceContractNotes !== serviceContractAttributes.serviceContractNotes

    if (!isNotesUpdated && !isStatusUpdated && !isStartDateUpdated && !isEndDateUpdated) {
      closeCallback(device)
      return false
    }

    if (!validate(serviceContractAttributes)) {
      setIsSaving(true)

      if (serviceContractAttributes.serviceContractStatus === 'Active' && serviceContractAttributes.serviceContractEndAt && !moment(serviceContractAttributes.serviceContractEndAt).isAfter(moment(new Date()))) {
        serviceContractAttributes.serviceContractStatus = 'Expired'
      } else if (serviceContractAttributes.serviceContractStatus === 'Expired' && serviceContractAttributes.serviceContractEndAt && moment(serviceContractAttributes.serviceContractEndAt).isAfter(moment(new Date()))) {
        serviceContractAttributes.serviceContractStatus = 'Active'
      }

      await Api.Device.updateServiceContract(device.dealerId, device.deviceId, serviceContractAttributes)
        .then((response) => {
          isStatusUpdated && sendGA4Event('Service Contract Updated', serviceContractAttributes.serviceContractStatus)
          isStartDateUpdated && sendGA4Event('Service Contract Start Date Added', serviceContractAttributes.serviceContractStartAt)
          isEndDateUpdated && sendGA4Event('Service Contract End Date Added', serviceContractAttributes.serviceContractEndAt)
          isNotesUpdated && sendGA4Event('Service Contract Notes Added', serviceContractAttributes.serviceContractNotes)
          closeCallback(response)
        }).catch(async (error) => {
          if (error.status === 400) {
            const errorResponse = await error.response.json()
            setErrorMessage({ ...errorMessage, apiError: errorResponse?.base[0].name })
          }
        }).finally(() => setIsSaving(false))
    } else {
      setIsSaving(false)
    }
  }, [device, serviceContractAttributes, validate, closeCallback, errorMessage])

  const sendGA4Event = (actionText, getByLabelText) => {
    GA4.sendEvent({ action: actionText, label: getByLabelText, category: 'Customer Detail Page' })
  }

  const serviceContractStatus = serviceContractAttributes.serviceContractStatus === null ? 'None' : serviceContractAttributes.serviceContractStatus

  return (
    <Modal title="Service Contract" variant="sm" isDisplayed closeCallback={() => closeCallback(device)}>
      <S.Container>
        <div>
          <S.CustomerName> {customerName} </S.CustomerName>
          <div><S.Device>AUID: </S.Device><span>{device.deviceId}</span> </div>
        </div>
        <S.StatusContainer>
          <span> Contract status: </span>
          <S.ContractStatus status={serviceContractStatus}> {serviceContractStatus}</S.ContractStatus>
        </S.StatusContainer>
        <div>
          <S.Instruction> To make an adjustment to the contract status in Diagnostics for this device, enter the dates below and click Save.  </S.Instruction>
        </div>
        <S.DateContainer>
          <div>
            <DatePicker
              label="Start Date"
              value={serviceContractAttributes.serviceContractStartAt}
              onChangeCallback={(value) => onChangeDate('startDate', value)}
            />
            <S.errorMessage> {errorMessage.serviceContractStartAt} </S.errorMessage>
          </div>
          <div>
            <DatePicker
              label="End Date*"
              value={serviceContractAttributes.serviceContractEndAt}
              onChangeCallback={(value) => onChangeDate('endDate', value)}
            />
            <S.errorMessage> {errorMessage.serviceContractEndAt} </S.errorMessage>
          </div>
        </S.DateContainer>
        <div>
          <S.errorMessage> {errorMessage.apiError} </S.errorMessage>
        </div>
        <div>
          <Textarea isRequired={false} label="" placeholder="Add notes..." rows={5} value={serviceContractAttributes.serviceContractNotes ?? ''} onChange={event => setServiceContractAttributes({
            ...serviceContractAttributes, serviceContractNotes: scrubText(event.target.value, true)
          })}
          />
        </div>
        <S.ButtonContainer>
          <Button decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={() => closeCallback(device)} />
          <Button decoration="positive" enabled={!isSaving} text="Save" onPress={onSaveServiceContract} />
        </S.ButtonContainer>
      </S.Container>
    </Modal>
  )
}

export default ServiceContractModal

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    gap: 25px;
 `,
  CustomerName: styled.span`
    color: ${color('primaryText')};
    font-weight: 600;
 `,
  Device: styled.span`
    color: ${color('primaryText')};
    font-weight: 500;
 `,
  ContractStatus: styled.span.withConfig({
    shouldForwardProp: (prop) => !['status'].includes(prop)
  })<{ status: string }>`
    color: ${({ status }) => status && color(`serviceContract${status}`)};
    font-weight: 600;
 `,
  Instruction: styled.span`
    color: ${color('primaryText')};
    font-weight: 400;
 `,
  StatusContainer: styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
 `,
  ButtonContainer: styled(ButtonContainer)`
    height: auto;
    padding-top: 0px;
    margin-top: 0px;
    border-top: 0px;
 `,
  DatePickerLabel: styled.span`
    color: ${color('primaryText')};
    font-weight: 400;
 `,
  DateContainer: styled.div`
    display: flex;
    gap: 20px;
    margin-top: 20px;
    justify-content: space-between;
 `,
  errorMessage: styled.span`
    display: block;
    font-weight: 700;
    color: ${color('errorMessage')};
    font-size: 12px;
 `
}
